import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { chatbotService } from '../../../services/ChatbotService';
import type { ChatbotTrainingExampleWithIdentity } from '../../../types/chatbot';
import {
  downloadTrainingCorrectionsFile,
  parseTrainingCorrectionsFile,
  serializeTrainingCorrectionsFile,
} from '../chatbotTrainingCorrectionsFile';
import { chatbotQueryKeys } from '../chatbotQueryKeys';
import type { TrainingCorrectionsProgress } from '../trainingCorrectionsProgress';

export function useCreateChatbotTrainingExampleMutation(chatbotId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      question: string;
      answer: string;
      sortOrder?: number | null;
    }) =>
      chatbotService.createTrainingExample({
        chatbotId,
        question: input.question,
        answer: input.answer,
        sortOrder: input.sortOrder,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...chatbotQueryKeys.chatbot(chatbotId), 'trainingExamples'],
      });
    },
  });
}

export function useUpdateChatbotTrainingExampleMutation(chatbotId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      example: ChatbotTrainingExampleWithIdentity;
      question: string;
      answer: string;
      sortOrder: number | null;
    }) =>
      chatbotService.updateTrainingExample({
        chatbotId,
        example: input.example,
        question: input.question,
        answer: input.answer,
        sortOrder: input.sortOrder,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...chatbotQueryKeys.chatbot(chatbotId), 'trainingExamples'],
      });
      void queryClient.invalidateQueries({
        queryKey: chatbotQueryKeys.trainingExample(
          chatbotId,
          variables.example.id,
        ),
      });
    },
  });
}

export function useDeleteChatbotTrainingExampleMutation(chatbotId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (exampleId: string) =>
      chatbotService.deleteTrainingExample(chatbotId, exampleId),
    onSuccess: (_data, exampleId) => {
      void queryClient.invalidateQueries({
        queryKey: [...chatbotQueryKeys.chatbot(chatbotId), 'trainingExamples'],
      });
      void queryClient.invalidateQueries({
        queryKey: chatbotQueryKeys.trainingExample(chatbotId, exampleId),
      });
    },
  });
}

export function useDeleteAllChatbotTrainingExamplesMutation(chatbotId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<TrainingCorrectionsProgress | null>(
    null,
  );

  const mutation = useMutation({
    mutationFn: async () => {
      setProgress(null);
      const examples = await chatbotService.listAllTrainingExamples(chatbotId);
      const total = examples.length;
      setProgress({ completed: 0, total });

      for (let index = 0; index < examples.length; index += 1) {
        const example = examples[index];
        if (!example) {
          continue;
        }
        await chatbotService.deleteTrainingExample(chatbotId, example.id);
        setProgress({ completed: index + 1, total });
      }

      return total;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...chatbotQueryKeys.chatbot(chatbotId), 'trainingExamples'],
      });
    },
    onSettled: () => {
      setProgress(null);
    },
  });

  return { ...mutation, progress };
}

export function useExportChatbotTrainingExamplesMutation(
  chatbotId: string,
  chatbotName: string,
) {
  const [progress, setProgress] = useState<TrainingCorrectionsProgress | null>(
    null,
  );

  const mutation = useMutation({
    mutationFn: async () => {
      setProgress(null);
      const examples = await chatbotService.listAllTrainingExamples(
        chatbotId,
        setProgress,
      );
      const contents = serializeTrainingCorrectionsFile(
        examples.map((example) => ({
          question: example.question,
          answer: example.answer,
          sortOrder: example.sortOrder ?? null,
        })),
      );
      downloadTrainingCorrectionsFile(chatbotName, contents);
      return examples.length;
    },
    onSettled: () => {
      setProgress(null);
    },
  });

  return { ...mutation, progress };
}

export function useImportChatbotTrainingExamplesMutation(chatbotId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<TrainingCorrectionsProgress | null>(
    null,
  );

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      setProgress(null);
      const text = await file.text();
      const parsed = parseTrainingCorrectionsFile(text);
      const total = parsed.corrections.length;
      setProgress({ completed: 0, total });

      for (let index = 0; index < parsed.corrections.length; index += 1) {
        const correction = parsed.corrections[index];
        if (!correction) {
          continue;
        }
        await chatbotService.createTrainingExample({
          chatbotId,
          question: correction.question,
          answer: correction.answer,
          sortOrder: correction.sortOrder,
        });
        setProgress({ completed: index + 1, total });
      }

      return parsed.count;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...chatbotQueryKeys.chatbot(chatbotId), 'trainingExamples'],
      });
    },
    onSettled: () => {
      setProgress(null);
    },
  });

  return { ...mutation, progress };
}
